// src/pages/Support/CentroAjudaPage.jsx

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Button,
  TextField,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Person as PersonIcon,
  HowToReg as HowToRegIcon,
  Payment as PaymentIcon,
  PlayCircle as PlayCircleIcon,
  Assignment as AssignmentIcon,
  Book as BookIcon,
  LiveHelp as LiveHelpIcon,
  ContactSupport as ContactSupportIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  VideoLibrary as VideoLibraryIcon,
  MenuBook as MenuBookIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    {...other}
  >
    {value === index && (
      <Box sx={{ p: 3 }}>
        {children}
      </Box>
    )}
  </div>
);

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

const TermSection = ({ title, icon: Icon, children }) => (
  <Paper sx={{ p: 3, mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <Icon color="primary" sx={{ mr: 2, fontSize: 28 }} />
      <Typography variant="h6">{title}</Typography>
    </Box>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Paper>
);

TermSection.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  children: PropTypes.node.isRequired,
};

const CentroAjudaPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentTab, setCurrentTab] = useState(0);

  // Guias por perfil de usuário
  const userGuides = [
    {
      title: 'Guia do Estudante',
      icon: PersonIcon,
      topics: [
        {
          title: 'Primeiros Passos',
          description: 'Como começar a usar o ZenKroll',
          link: '/ajuda/estudante/primeiros-passos',
          icon: PlayCircleIcon,
        },
        {
          title: 'Fazendo uma Pré-inscrição',
          description: 'Passo a passo completo do processo',
          link: '/ajuda/estudante/pre-inscricao',
          icon: AssignmentIcon,
        },
        {
          title: 'Pagamentos',
          description: 'Como efetuar e gerenciar pagamentos',
          link: '/ajuda/estudante/pagamentos',
          icon: PaymentIcon,
        },
      ],
    },
    {
      title: 'Guia da Instituição',
      icon: SchoolIcon,
      topics: [
        {
          title: 'Gerindo Campanhas',
          description: 'Como criar e gerir campanhas',
          link: '/ajuda/instituicao/campanhas',
          icon: CampaignIcon,
        },
        {
          title: 'Análise de Pré-inscrições',
          description: 'Como avaliar e processar pré-inscrições',
          link: '/ajuda/instituicao/analise',
          icon: HowToRegIcon,
        },
        {
          title: 'Relatórios e Estatísticas',
          description: 'Como utilizar as ferramentas de análise',
          link: '/ajuda/instituicao/relatorios',
          icon: AssignmentIcon,
        },
      ],
    },
  ];

  // Tutoriais em vídeo
  const videoTutorials = [
    {
      title: 'Como fazer uma Pré-inscrição',
      thumbnail: '/images/tutorial-1.jpg',
      duration: '5:30',
      category: 'Estudante',
      link: '/tutoriais/pre-inscricao',
    },
    {
      title: 'Gerindo Campanhas',
      thumbnail: '/images/tutorial-2.jpg',
      duration: '8:45',
      category: 'Instituição',
      link: '/tutoriais/campanhas',
    },
    {
      title: 'Sistema de Pagamentos',
      thumbnail: '/images/tutorial-3.jpg',
      duration: '6:15',
      category: 'Geral',
      link: '/tutoriais/pagamentos',
    },
  ];

  // Documentação detalhada
  const documentation = [
    {
      category: 'Pré-inscrições',
      icon: AssignmentIcon,
      docs: [
        { title: 'Processo de Pré-inscrição', link: '/docs/pre-inscricao/processo' },
        { title: 'Documentos Necessários', link: '/docs/pre-inscricao/documentos' },
        { title: 'Status e Acompanhamento', link: '/docs/pre-inscricao/status' },
      ],
    },
    {
      category: 'Pagamentos',
      icon: PaymentIcon,
      docs: [
        { title: 'Métodos de Pagamento', link: '/docs/pagamentos/metodos' },
        { title: 'Comprovantes e Verificação', link: '/docs/pagamentos/comprovantes' },
        { title: 'Prazos e Políticas', link: '/docs/pagamentos/prazos' },
      ],
    },
    {
      category: 'Campanhas',
      icon: CampaignIcon,
      docs: [
        { title: 'Criação de Campanhas', link: '/docs/campanhas/criacao' },
        { title: 'Gestão de Vagas', link: '/docs/campanhas/vagas' },
        { title: 'Análise de Resultados', link: '/docs/campanhas/resultados' },
      ],
    },
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 6,
          mb: 4,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" gutterBottom>
            Centro de Ajuda
          </Typography>
          <Typography variant="h6" gutterBottom>
            Encontre tudo que precisa para usar o ZenKroll
          </Typography>
          
          {/* Barra de Pesquisa */}
          <Paper
            sx={{
              p: 2,
              mt: 4,
              maxWidth: 600,
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <TextField
              fullWidth
              placeholder="O que você procura?"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mr: 1 }}
            />
            <IconButton color="primary">
              <SearchIcon />
            </IconButton>
          </Paper>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ flex: 1, mb: 4 }}>
        {/* Tabs de Navegação */}
        <Paper sx={{ mb: 4 }}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            variant="fullWidth"
          >
            <Tab icon={<MenuBookIcon />} label="Guias" />
            <Tab icon={<VideoLibraryIcon />} label="Tutoriais em Vídeo" />
            <Tab icon={<BookIcon />} label="Documentação" />
            <Tab icon={<LiveHelpIcon />} label="Suporte" />
          </Tabs>
        </Paper>

        {/* Conteúdo das Tabs */}
        <TabPanel value={currentTab} index={0}>
          <Grid container spacing={4}>
            {userGuides.map((guide, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <guide.icon color="primary" sx={{ fontSize: 40, mr: 2 }} />
                    <Typography variant="h5">{guide.title}</Typography>
                  </Box>
                  <List>
                    {guide.topics.map((topic, topicIndex) => (
                      <ListItem
                        key={topicIndex}
                        component={Link}
                        to={topic.link}
                        sx={{
                          mb: 2,
                          borderRadius: 1,
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemIcon>
                          <topic.icon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={topic.title}
                          secondary={topic.description}
                        />
                        <ArrowForwardIcon color="action" />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Grid container spacing={3}>
            {videoTutorials.map((tutorial, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height="140"
                    image={tutorial.thumbnail}
                    alt={tutorial.title}
                  />
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {tutorial.title}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Chip label={tutorial.category} size="small" />
                      <Typography variant="body2" color="text.secondary">
                        {tutorial.duration}
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      startIcon={<PlayCircleIcon />}
                      component={Link}
                      to={tutorial.link}
                      fullWidth
                    >
                      Assistir Tutorial
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Grid container spacing={4}>
            {documentation.map((category, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <category.icon color="primary" sx={{ mr: 2 }} />
                    <Typography variant="h6">{category.category}</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <List>
                    {category.docs.map((doc, docIndex) => (
                      <ListItem
                        key={docIndex}
                        component={Link}
                        to={doc.link}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemText primary={doc.title} />
                        <ArrowForwardIcon color="action" fontSize="small" />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={currentTab} index={3}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Precisa de Ajuda?
                </Typography>
                <Typography paragraph>
                  Nossa equipe de suporte está disponível para ajudar você com qualquer dúvida.
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<ContactSupportIcon />}
                  component={Link}
                  to="/suporte"
                  fullWidth
                >
                  Contatar Suporte
                </Button>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Horário de Atendimento
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Segunda a Sexta"
                      secondary="08:00 - 18:00"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Sábado"
                      secondary="09:00 - 13:00"
                    />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </Container>

      <Footer />
    </Box>
  );
};

CentroAjudaPage.propTypes = {
  // Nenhuma prop recebida diretamente pelo componente
};

export default CentroAjudaPage;
